import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as StatusIcon,
  Warning as AlertIcon,
  Visibility as ViewIcon,
  Lightbulb as LedIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import type { Camera } from '../../types/raspi';
import { useCameraStore } from '../../store/cameraStore';
import CameraViewDialog from '../dialogs/CameraViewDialog';
import { DeleteButton } from '../DeleteButton';
import { sendLedControlCommand } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useUnauthorizedAction } from '../../App';
import { useTheme } from '@mui/material/styles';

interface CameraCardProps {
  cameraId: string;
  cameraData: Camera | null;
}

const CameraCard: React.FC<CameraCardProps> = ({
  cameraId,
  cameraData
}) => {
  const theme = useTheme();
  const { fetchAllCameras, updateCameraFromUser } = useCameraStore();
  
  // Create stable reference for fetch function to prevent infinite re-renders
  const fetchAllCamerasRef = useRef(fetchAllCameras);
  
  const [gaugeIdValue, setGaugeIdValue] = useState(cameraData?.camera_gauge_id || '');
  const [ledIdValue, setLedIdValue] = useState(cameraData?.camera_led_id || '');
  const [gaugeIdError, setGaugeIdError] = useState<string | null>(null);
  const [ledIdError, setLedIdError] = useState<string | null>(null);
  const [isUpdatingGaugeId, setIsUpdatingGaugeId] = useState(false);
  const [isUpdatingLedId, setIsUpdatingLedId] = useState(false);
  const [showCameraView, setShowCameraView] = useState(false);
  // Removed: const [isStreaming, setIsStreaming] = useState(false);
  
  // Track unsaved changes for visual feedback
  const [hasUnsavedGaugeId, setHasUnsavedGaugeId] = useState(false);
  const [hasUnsavedLedId, setHasUnsavedLedId] = useState(false);

  // Alert description visibility state
  const [showAlertDescription, setShowAlertDescription] = useState<boolean>(false);

  // Fetch camera data when component mounts
  useEffect(() => {
    fetchAllCamerasRef.current();
  }, []);

  // Update values when camera data changes
  useEffect(() => {
    setGaugeIdValue(cameraData?.camera_gauge_id || '');
    setLedIdValue(cameraData?.camera_led_id || '');
    setHasUnsavedGaugeId(false);
    setHasUnsavedLedId(false);
  }, [cameraData?.camera_gauge_id, cameraData?.camera_led_id]);

  // Get RASPI ID from camera ID (assuming format: RASPI08_CAM1)
  const getRaspiId = (): string => {
    if (!cameraId) return '';
    const parts = cameraId.split('_');
    return parts.length >= 2 ? parts[0] : '';
  };

  // Helper functions for status and alert icons/colors
  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RUN':
        return <StatusIcon sx={{ color: '#4caf50', fontSize: theme.iconSizes.small }} />;
      case 'SLEEP':
        return <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
      case 'DISCONNECTED':
        return <StatusIcon sx={{ color: '#f44336', fontSize: theme.iconSizes.small }} />;
      default:
        return <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RUN':
        return '#4caf50'; // Green - camera is running
      case 'SLEEP':
        return '#9e9e9e'; // Gray - camera is sleeping
      case 'DISCONNECTED':
        return '#f44336'; // Red - camera is disconnected
      default:
        return '#9e9e9e'; // Gray - unknown status
    }
  };

  const getAlertIcon = (alertLevel: string) => {
    switch (alertLevel?.toUpperCase()) {
      case 'HIGH':
        return <AlertIcon sx={{ color: '#f44336', fontSize: theme.iconSizes.small }} />;
      case 'NO':
        return <AlertIcon sx={{ color: '#4caf50', fontSize: theme.iconSizes.small }} />;
      default:
        return <AlertIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
    }
  };

  const getAlertColor = (alertLevel: string) => {
    switch (alertLevel?.toUpperCase()) {
      case 'HIGH':
        return '#f44336'; // Red - critical alert
      case 'NO':
        return '#4caf50'; // Green - no alerts
      default:
        return '#9e9e9e'; // Gray - unknown alert level
    }
  };

  const validateGaugeId = (value: string): boolean => {
    if (!value.trim()) {
      setGaugeIdError('Gauge ID is required');
      return false;
    }
    
    // Check if it starts with 'G' followed by a number (warning only, not preventing)
    const gaugePattern = /^G\d+$/i;
    if (!gaugePattern.test(value)) {
      setGaugeIdError('Warning: Gauge ID should be in format "G" + number (e.g., G001)');
      return true; // Still allow saving, just show warning
    }
    
    setGaugeIdError(null);
    return true;
  };

  const handleGaugeIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setGaugeIdValue(value);
    setHasUnsavedGaugeId(value !== cameraData?.camera_gauge_id);
    validateGaugeId(value);
  };

  const handleLedIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLedIdValue(value);
    setHasUnsavedLedId(value !== cameraData?.camera_led_id);
  };

  const handleSaveGaugeId = async () => {
    if (!cameraId || !cameraData) return;
    
    const isValid = validateGaugeId(gaugeIdValue);
    if (!isValid) return;
    
    setIsUpdatingGaugeId(true);
    try {
      const success = await updateCameraFromUser(cameraId, { camera_gauge_id: gaugeIdValue });
      
      if (success) {
        setHasUnsavedGaugeId(false);
        setGaugeIdError(null);
      } else {
        setGaugeIdError('Failed to update gauge ID');
      }
    } catch (error) {
      console.error('Error updating gauge ID:', error);
      setGaugeIdError('Failed to update gauge ID');
    } finally {
      setIsUpdatingGaugeId(false);
    }
  };

  const handleSaveLedId = async () => {
    if (!cameraId || !cameraData) return;
    
    setIsUpdatingLedId(true);
    try {
      const success = await updateCameraFromUser(cameraId, { camera_led_id: ledIdValue });
      
      if (success) {
        setHasUnsavedLedId(false);
        setLedIdError(null);
      } else {
        setLedIdError('Failed to update LED ID');
      }
    } catch (error) {
      console.error('Error updating LED ID:', error);
      setLedIdError('Failed to update LED ID');
    } finally {
      setIsUpdatingLedId(false);
    }
  };

  const handleViewCamera = () => {
    setShowCameraView(true);
  };

  const handleToggleLED = async () => {
    if (!cameraId || !cameraData?.camera_led_id) {
      console.warn('Cannot toggle LED: missing camera ID or LED ID');
      return;
    }
    
    const raspiId = getRaspiId();
    if (!raspiId) {
      console.warn('Cannot toggle LED: cannot determine RASPI ID');
      return;
    }
    
    try {
      const success = await sendLedControlCommand(raspiId, cameraId, cameraData.camera_led_id, 'toggle');
      if (!success) {
        console.error('Failed to send LED toggle command');
      }
    } catch (error) {
      console.error('Error toggling LED:', error);
    }
  };

  const handleAlertIconClick = () => {
    setShowAlertDescription(!showAlertDescription);
  };

  const getRole = useAuthStore((state) => state.getRole);
  const isViewer = getRole() === 'viewer' || getRole() === 'public';
  const triggerUnauthorized = useUnauthorizedAction();

  return (
    <Card sx={{ backgroundColor: 'white', mb: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gridTemplateRows: 'auto',
          gap: 1,
          gridTemplateAreas: `
            "camera_id camera_details camera_control camera_monitor"
          `
        }}>
          {/* Camera ID Box */}
          <Box sx={{ 
            gridArea: 'camera_id',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"CAMERA ID"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
              {/* Camera ID without border */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 2 }}>
                <Typography variant="entityId" sx={{ color: 'text.primary' }}>
                  {cameraData?.camera_id || cameraId || 'None'}
                </Typography>
              </Box>
              
              {/* Status and Alert icons in one line */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flex: 1 }}>
                {/* Status */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: cameraData ? 
                    (cameraData?.camera_status === 'RUN' ? '#e8f5e8' : 'transparent') : 'transparent',
                  border: cameraData ? 
                    (cameraData?.camera_status === 'RUN' ? '1px solid #c8e6c9' : 'none') : 'none'
                }}>
                  <Tooltip title={''} arrow>
                    <span>{cameraData ? getStatusIcon(cameraData?.camera_status || 'UNKNOWN') : <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />}</span>
                  </Tooltip>
                  <Typography variant="iconValueText" sx={{ textAlign: 'center', color: cameraData ? getStatusColor(cameraData?.camera_status || 'UNKNOWN') : '#9e9e9e' }}>
                    {cameraData ? (cameraData?.camera_status || 'UNKNOWN') : 'N/A'}
                  </Typography>
                  <Typography variant="iconLabelText" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    STATUS
                  </Typography>
                </Box>
                
                {/* Alert */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: cameraData ? 
                    (cameraData?.camera_alert === 'HIGH' ? '#ffebee' : 'transparent') : 'transparent',
                  border: cameraData ? 
                    (cameraData?.camera_alert === 'HIGH' ? '1px solid #ffcdd2' : 'none') : 'none'
                }}>
                  <Tooltip title={cameraData?.camera_alert_description || ''} arrow>
                    <span 
                      onClick={handleAlertIconClick}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'opacity 0.2s ease',
                        opacity: 1
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      {cameraData ? getAlertIcon(cameraData?.camera_alert || 'NO') : <AlertIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />}
                    </span>
                  </Tooltip>
                  <Typography variant="iconValueText" sx={{ textAlign: 'center', color: cameraData ? getAlertColor(cameraData?.camera_alert || 'NO') : '#9e9e9e' }}>
                    {cameraData ? (cameraData?.camera_alert || 'NO') : 'N/A'}
                  </Typography>
                  <Typography variant="iconLabelText" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    ALERT
                  </Typography>
                </Box>
                
                {/* Delete - always visible, but only clickable when DISCONNECTED */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0,
                  p: 0,
                  borderRadius: 1
                }}>
                  <DeleteButton
                    onDelete={() => useCameraStore.getState().deleteCamera(cameraId)}
                    entityType="camera"
                    entityId={cameraId}
                    disabled={cameraData?.camera_status !== 'DISCONNECTED'}
                  />
                </Box>
              </Box>
              
              {/* Alert Description - shown when alert icon is clicked */}
              {showAlertDescription && cameraData?.camera_alert_description && (
                <Box sx={{ 
                  mt: 1, 
                  p: 1, 
                  backgroundColor: '#fff3e0', 
                  border: '1px solid #ffe0b2',
                  borderRadius: 1,
                  borderLeft: '4px solid #ff9800'
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.7rem',
                      color: '#e65100',
                      fontWeight: 'medium',
                      display: 'block'
                    }}
                  >
                    Alert: {cameraData?.camera_alert_description}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Camera Details Box */}
          <Box sx={{ 
            gridArea: 'camera_details',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"DETAILS"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Image Resolution and Type in same row */}
              <Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
                {/* Image Resolution */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={cameraData?.camera_resolution || '1920X1080'}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '8px 8px'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    IMAGE RES.
                  </Typography>
                </Box>
                
                {/* Type */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={cameraData?.camera_model || 'PEN'}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '8px 8px'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: ledIdError ? '#f44336' : 
                                     hasUnsavedLedId ? '#ff9800' : '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: ledIdError ? '#f44336' : 
                                     hasUnsavedLedId ? '#ff9800' : '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: ledIdError ? '#f44336' : 
                                     hasUnsavedLedId ? '#ff9800' : '#1976d2',
                        },
                      },
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    TYPE
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Camera Control Box */}
          <Box sx={{ 
            gridArea: 'camera_control',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"CONTROLS"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Row 1: GAUGE_ID input field, SAVE button, and VIEW button */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* GAUGE_ID input field */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={gaugeIdValue}
                    onChange={handleGaugeIdChange}
                    variant="outlined"
                    size="small"
                    placeholder="Enter Gauge ID"
                    error={!!gaugeIdError}
                    disabled={isUpdatingGaugeId}
                    helperText={gaugeIdError}
                    InputProps={{
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '6px 6px'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: gaugeIdError ? '#f44336' : 
                                     hasUnsavedGaugeId ? '#ff9800' : '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: gaugeIdError ? '#f44336' : 
                                     hasUnsavedGaugeId ? '#ff9800' : '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: gaugeIdError ? '#f44336' : 
                                     hasUnsavedGaugeId ? '#ff9800' : '#1976d2',
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.6rem',
                        margin: '2px 0 0 0',
                        lineHeight: 1.2
                      }
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    GAUGE_ID
                  </Typography>
                </Box>
                
                {/* SAVE button for GAUGE_ID */}
                <Tooltip title={hasUnsavedGaugeId ? "Save Gauge ID" : "No changes to save"}>
                  <span>
                    <IconButton
                      onClick={isViewer ? () => triggerUnauthorized() : handleSaveGaugeId}
                      disabled={!hasUnsavedGaugeId || isUpdatingGaugeId}
                      size="small"
                      sx={{ 
                        color: hasUnsavedGaugeId ? '#ff9800' : '#9e9e9e',
                        '&:hover': {
                          color: hasUnsavedGaugeId ? '#f57c00' : '#9e9e9e',
                        }
                      }}
                    >
                      <SaveIcon sx={{ fontSize: '0.8rem' }} />
                    </IconButton>
                  </span>
                </Tooltip>
                
                {/* VIEW button */}
                <Button 
                  onClick={isViewer ? () => triggerUnauthorized() : handleViewCamera}
                  variant="outlined"
                  size="small"
                  startIcon={<ViewIcon sx={{ fontSize: '0.8rem' }} />}
                  sx={{ 
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    minWidth: 'fit-content',
                    padding: '4px 8px'
                  }}
                >
                  VIEW
                </Button>
              </Box>
              
              {/* Row 2: LED_ID input field, SAVE button, and LED button */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* LED_ID input field */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={ledIdValue}
                    onChange={handleLedIdChange}
                    variant="outlined"
                    size="small"
                    placeholder="Enter LED ID"
                    error={!!ledIdError}
                    disabled={isUpdatingLedId}
                    InputProps={{
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '6px 6px'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    LED_ID
                  </Typography>
                </Box>
                
                {/* SAVE button for LED_ID */}
                <Tooltip title={hasUnsavedLedId ? "Save LED ID" : "No changes to save"}>
                  <span>
                    <IconButton
                      onClick={isViewer ? () => triggerUnauthorized() : handleSaveLedId}
                      disabled={!hasUnsavedLedId || isUpdatingLedId}
                      size="small"
                      sx={{ 
                        color: hasUnsavedLedId ? '#ff9800' : '#9e9e9e',
                        '&:hover': {
                          color: hasUnsavedLedId ? '#f57c00' : '#9e9e9e',
                        }
                      }}
                    >
                      <SaveIcon sx={{ fontSize: '0.8rem' }} />
                    </IconButton>
                  </span>
                </Tooltip>
                
                {/* LED button */}
                <Button 
                  onClick={isViewer ? () => triggerUnauthorized() : handleToggleLED}
                  variant="outlined"
                  size="small"
                  startIcon={<LedIcon sx={{ fontSize: '0.8rem' }} />}
                  sx={{ 
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    minWidth: 'fit-content',
                    padding: '4px 8px'
                  }}
                >
                  LED
                </Button>
              </Box>
              
            </Box>
          </Box>
          
          {/* Camera Monitor Box */}
          <Box sx={{ 
            gridArea: 'camera_monitor',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"MONITORING"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Progress Bar Container - same design as ARDUINO PORT */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', paddingBottom: '2px' }}>
                <Box sx={{ 
                  width: '100%',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  padding: '8px 8px',
                  backgroundColor: 'white',
                  '&:hover': {
                    borderColor: '#bbb',
                  },
                  '&.Mui-focused': {
                    borderColor: '#1976d2',
                  },
                }}>
                  <Box sx={{ 
                    width: '100%', 
                    height: 20, 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <Box sx={{ 
                      width: `${(() => {
                        if (!cameraData || !cameraData.camera_image_total || cameraData.camera_image_total <= 0) return 0;
                        const percentage = (cameraData.camera_image_counter || 0) / cameraData.camera_image_total * 100;
                        return Math.min(Math.max(percentage, 0), 100);
                      })()}%`, 
                      height: '100%', 
                      backgroundColor: '#4caf50',
                      transition: 'width 0.3s ease'
                    }} />
                    <Typography 
                      variant="sectionHeader"
                      sx={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontWeight: 'bold',
                        color: 'black',
                        textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                        zIndex: 1
                      }}
                    >
                      {(() => {
                        if (!cameraData || !cameraData.camera_image_total || cameraData.camera_image_total <= 0) return '0%';
                        const percentage = (cameraData.camera_image_counter || 0) / cameraData.camera_image_total * 100;
                        const clampedPercentage = Math.min(Math.max(percentage, 0), 100);
                        return `${Math.round(clampedPercentage)}%`;
                      })()}
                    </Typography>
                  </Box>
                </Box>
                <Typography 
                  variant="sectionHeader"
                  sx={{ 
                    position: 'absolute',
                    top: '100%',
                    left: '8px',
                    marginTop: '0px',
                    color: 'text.secondary',
                    backgroundColor: 'white',
                    padding: '0 2px',
                    lineHeight: 1.2,
                    whiteSpace: 'normal'
                  }}
                >
                  IMAGES: {cameraData?.camera_image_counter || 0}/{cameraData?.camera_image_total || 0}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
      
      {/* Camera View Dialog */}
      <CameraViewDialog
        open={showCameraView}
        onClose={() => setShowCameraView(false)}
        cameraId={cameraId}
        raspiId={getRaspiId()}
      />
    </Card>
  );
};

export default CameraCard; 